import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { observer } from 'mobx-react'
import moment from 'moment'
import Container from '../layout/Container'
import RootStore from '../../store/Root'
import Button from '../utils/Button'
import { getAppBaseUrl } from '../../api/api'

interface Props {
    rootStore: RootStore
}

const formatDateTime = (value?: string) => {
    if (!value) {
        return '-'
    }

    return moment(value).format('MM/DD/YYYY HH:mm:ss')
}

const resolveMediaUrl = (value?: string) => {
    if (!value) return ''
    if (value.startsWith('http://') || value.startsWith('https://')) {
        return value
    }

    return `${getAppBaseUrl()}${value.startsWith('/') ? value : `/${value}`}`
}

const SecureMediaPreview: React.FC<{
    mediaType?: string
    previewUrl?: string
    alt?: string
}> = ({ mediaType, previewUrl, alt }) => {
    const [blobUrl, setBlobUrl] = useState('')

    useEffect(() => {
        if (!previewUrl || (mediaType !== 'image' && mediaType !== 'video')) {
            setBlobUrl('')
            return undefined
        }

        let isMounted = true
        let nextBlobUrl = ''

        axios.get(resolveMediaUrl(previewUrl), {
            responseType: 'blob'
        }).then((response) => {
            if (!isMounted) return
            nextBlobUrl = URL.createObjectURL(response.data)
            setBlobUrl(nextBlobUrl)
        }).catch(() => {
            if (isMounted) {
                setBlobUrl('')
            }
        })

        return () => {
            isMounted = false
            if (nextBlobUrl) {
                URL.revokeObjectURL(nextBlobUrl)
            }
        }
    }, [mediaType, previewUrl])

    if (!previewUrl) {
        return null
    }

    if (mediaType === 'image') {
        return blobUrl
            ? (
                <img
                    src={blobUrl}
                    alt={alt || 'QAI media preview'}
                    className='img-fluid rounded border'
                    style={{ maxHeight: '240px', objectFit: 'cover' }}
                />
            )
            : <div className='small text-muted'>Loading preview...</div>
    }

    if (mediaType === 'video') {
        return blobUrl
            ? (
                <video
                    src={blobUrl}
                    className='img-fluid rounded border'
                    style={{ maxHeight: '240px' }}
                    controls
                    muted
                />
            )
            : <div className='small text-muted'>Loading preview...</div>
    }

    return (
        <a href={resolveMediaUrl(previewUrl)} target='_blank' rel='noreferrer'>
            Open Media
        </a>
    )
}

const QaiModels: React.FC<Props> = ({ rootStore }) => {
    const { QaiStore } = rootStore
    const {
        models,
        selectedModel,
        selectedAgentKey,
        isLoading,
        isDetailLoading,
        isSyncing,
        isApiError,
        apiErrorMessage,
        syncMessage,
        getModels,
        getModelDetail,
        syncAllModels,
        syncSelectedModel
    } = QaiStore

    useEffect(() => {
        getModels(true)
    }, [getModels])

    return (
        <Container rootStore={rootStore} redirectIfNotLoggedIn={true}>
            <div className='row py-2'>
                <div className='col-md-6'>
                    <h4>QAI Model Cache</h4>
                    <p className='text-muted mb-0'>
                        Models and details are read from Mongo cache. The cache is refreshed manually here or by QAI webhook notifications.
                    </p>
                </div>
                <div className='col-md-6 text-end'>
                    <Button
                        disabled={isSyncing}
                        type='button'
                        title='Sync All Models'
                        classes='btn-primary ms-2'
                        loading={isSyncing}
                        onClick={syncAllModels}
                        icon={null}
                    />
                    <Button
                        disabled={isSyncing || selectedAgentKey === ''}
                        type='button'
                        title='Sync Selected Model'
                        classes='btn-outline-primary ms-2'
                        loading={false}
                        onClick={syncSelectedModel}
                        icon={null}
                    />
                </div>
            </div>

            {isApiError && <div className='alert alert-danger'>{apiErrorMessage}</div>}
            {!isApiError && syncMessage !== '' && <div className='alert alert-success'>{syncMessage}</div>}

            <div className='row'>
                <div className='col-lg-5 mb-4'>
                    <div className='card h-100'>
                        <div className='card-header d-flex justify-content-between align-items-center'>
                            <span>Cached Models</span>
                            <span className='badge bg-secondary'>{models.length}</span>
                        </div>
                        <div className='card-body p-0'>
                            <div className='table-responsive'>
                                <table className='table table-hover mb-0'>
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Status</th>
                                            <th>Last Synced</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading && (
                                            <tr>
                                                <td colSpan={3} className='text-center py-4'>Loading models...</td>
                                            </tr>
                                        )}
                                        {!isLoading && models.length === 0 && (
                                            <tr>
                                                <td colSpan={3} className='text-center py-4'>No QAI models cached yet.</td>
                                            </tr>
                                        )}
                                        {!isLoading && models.map((model) => (
                                            <tr
                                                key={model.agent_key}
                                                className={selectedAgentKey === model.agent_key ? 'table-primary' : ''}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => getModelDetail(model.agent_key)}
                                            >
                                                <td>
                                                    <div className='fw-semibold'>{model.name || model.agent_key}</div>
                                                    <div className='small text-muted'>{model.agent_key}</div>
                                                </td>
                                                <td>{model.status || '-'}</td>
                                                <td>{formatDateTime(model.last_synced_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='col-lg-7 mb-4'>
                    <div className='card h-100'>
                        <div className='card-header'>Model Detail</div>
                        <div className='card-body'>
                            {isDetailLoading && <div>Loading model detail...</div>}
                            {!isDetailLoading && !selectedModel && (
                                <div className='text-muted'>Select a model from the list to view cached detail.</div>
                            )}
                            {!isDetailLoading && selectedModel && (
                                <>
                                    <div className='row mb-3'>
                                        <div className='col-md-8'>
                                            <h5 className='mb-1'>{selectedModel.name || selectedModel.agent_key}</h5>
                                            <div className='text-muted'>{selectedModel.agent_key}</div>
                                        </div>
                                        <div className='col-md-4 text-md-end'>
                                            <span className='badge bg-info text-dark'>{selectedModel.status || 'unknown'}</span>
                                        </div>
                                    </div>

                                    <div className='row mb-3'>
                                        <div className='col-md-6'>
                                            <div className='small text-muted'>Last Synced</div>
                                            <div>{formatDateTime(selectedModel.last_synced_at)}</div>
                                        </div>
                                        <div className='col-md-6'>
                                            <div className='small text-muted'>Detail Cached At</div>
                                            <div>{formatDateTime(selectedModel.detail_fetched_at)}</div>
                                        </div>
                                    </div>

                                    <div className='row mb-3'>
                                        <div className='col-md-4'>
                                            <div className='small text-muted'>Image Count</div>
                                            <div>{selectedModel.image_count || 0}</div>
                                        </div>
                                        <div className='col-md-4'>
                                            <div className='small text-muted'>Video Count</div>
                                            <div>{selectedModel.video_count || 0}</div>
                                        </div>
                                        <div className='col-md-4'>
                                            <div className='small text-muted'>Media Cached At</div>
                                            <div>{formatDateTime(selectedModel.media_fetched_at)}</div>
                                        </div>
                                    </div>

                                    <div className='mb-3'>
                                        <div className='small text-muted'>Summary</div>
                                        <div>{selectedModel.summary || '-'}</div>
                                    </div>

                                    <div className='mb-3'>
                                        <div className='small text-muted'>Tags</div>
                                        <div>
                                            {Array.isArray(selectedModel.tags) && selectedModel.tags.length > 0
                                                ? selectedModel.tags.join(', ')
                                                : '-'}
                                        </div>
                                    </div>

                                    <div className='mb-3'>
                                        <div className='small text-muted mb-2'>Thumbnail Preview</div>
                                        {selectedModel.thumbnail_urls?.md || selectedModel.thumbnail_url
                                            ? <SecureMediaPreview mediaType='image' previewUrl={selectedModel.thumbnail_urls?.md || selectedModel.thumbnail_url} alt={selectedModel.name || selectedModel.agent_key} />
                                            : <div>-</div>
                                        }
                                    </div>

                                    <div className='mb-3'>
                                        <div className='small text-muted'>Thumbnail URL</div>
                                        <div className='text-break'>{resolveMediaUrl(selectedModel.thumbnail_urls?.md || selectedModel.thumbnail_url) || '-'}</div>
                                    </div>

                                    <div className='mb-3'>
                                        <div className='small text-muted mb-2'>Media Items</div>
                                        {Array.isArray(selectedModel.media_items) && selectedModel.media_items.length > 0
                                            ? <div className='row'>
                                                {selectedModel.media_items.map((item) => (
                                                    <div className='col-md-6 mb-3' key={item.media_key}>
                                                        <div className='border rounded p-3 h-100'>
                                                            <div className='fw-semibold mb-1'>{item.media_key}</div>
                                                            <div className='small text-muted mb-2'>
                                                                {item.media_type || 'unknown'}{item.scope ? ` • ${item.scope}` : ''}
                                                            </div>
                                                            <div className='mb-2'>
                                                                {item.preview_url
                                                                    ? <SecureMediaPreview mediaType={item.media_type} previewUrl={item.preview_url} alt={item.description || item.media_key} />
                                                                    : <div>-</div>}
                                                            </div>
                                                            {item.original_url && (
                                                                <div className='small mb-2'>
                                                                    <a href={resolveMediaUrl(item.original_url)} target='_blank' rel='noreferrer'>Open original</a>
                                                                </div>
                                                            )}
                                                            <div className='small mb-1'><strong>Description:</strong> {item.description || '-'}</div>
                                                            <div className='small mb-1'><strong>Created:</strong> {formatDateTime(item.created_at)}</div>
                                                            <div className='small mb-1'><strong>User Key:</strong> {item.user_key || '-'}</div>
                                                            <div className='small text-break'><strong>Metadata:</strong> {item.metadata ? JSON.stringify(item.metadata) : '-'}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            : <div>-</div>
                                        }
                                    </div>

                                    <div>
                                        <div className='small text-muted mb-2'>Raw Detail Payload</div>
                                        <pre className='bg-light border rounded p-3 small overflow-auto' style={{ maxHeight: '420px' }}>
                                            {JSON.stringify(selectedModel.detail_payload || {}, null, 2)}
                                        </pre>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    )
}

export default observer(QaiModels)
